import React, { useContext, useMemo, useState } from 'react'

import { DataContext } from '../../../context/DataContext'

import Footer from '../Footer'

interface GroupedSection {
  section: number
  items: Collection[]
}

interface CollectionsProps {
  footer?: boolean
}

const Collections: React.FC<CollectionsProps> = ({ footer = false }) => {
  const { collections, companyColor } = useContext(DataContext) as { collections: Collection[] | null, companyColor: string }

  const [isHovered, setIsHovered] = useState<{module: number, section: number}>({module: -1, section: -1})

  const groupedSections = useMemo(() => {
    if (!collections || collections.length === 0) return []

    const grouped = collections.reduce<Record<number, Collection[]>>((acc, collection) => {
      const { section } = collection
      if (!acc[section]) {
        acc[section] = []
      }
      acc[section].push(collection)
      return acc
    }, {})

    return Object.entries(grouped)
      .map(([section, items]): GroupedSection => ({
        section: Number(section),
        items: items.sort((a, b) => a.module - b.module)
      }))
      .sort((a, b) => a.section - b.section)
  }, [collections])

  const handleRedirect = (url?: string) => {
    if (!url) return
    if (typeof window === 'undefined') return
    if (window.self !== window.top) {
      window.top!.location.href = url
    } else {
      window.location.href = url
    }
  }

  if (groupedSections.length === 0) return null

  return (
    <div className='flex flex-col gap-2 portrait:py-2 h-full w-full min-w-0'>
      {groupedSections.map(({ section, items }) => (
        <div
          key={`${section}`}
          className='flex items-start gap-2 portrait:px-4 overflow-x-auto scrollbar-hide w-full'
        >
          {items.map((collection) => {
            const hovered = collection.module === isHovered.module && collection.section === isHovered.section
            const aspectClass = ['aspect-card-1', 'aspect-card-2', 'aspect-card-3'][items.length - 1]
            return (
              <div
                key={`${collection.module}-${collection.section}`}
                className={`relative flex-shrink-0 min-w-28 flex-1 rounded-xl overflow-hidden ${aspectClass}`}
                onMouseEnter={() => {
                  setIsHovered({module: collection.module, section: collection.section})
                }}
                onMouseLeave={() => {
                  setIsHovered({module: -1, section: -1})
                }}
              >
                <div
                  className='absolute inset-0 rounded-xl overflow-hidden'
                  onClick={() => handleRedirect(collection.data.redirectUrl)}
                >
                  <img
                    src={`${collection.data.imageUrl}?v=${Date.now()}`}
                    alt={collection.data.title || 'Collection Image'}
                    className={`absolute inset-0 w-full h-full object-cover rounded-xl transition-transform duration-300 ease-in-out ${
                      hovered ? 'scale-125' : 'scale-100'
                    }`}
                  />
                  {collection.data.title &&
                    <div className={`absolute bottom-2 left-0 right-0 mx-auto rounded-full w-28 h-8 ${hovered ? 'bg-gray-100' : 'bg-white'} flex items-center justify-center`}>
                      <p className={`text-base font-medium ${companyColor}`}>{collection.data.title}</p>
                    </div>
                  }
                </div>
              </div>
            )
          })}
        </div>
      ))}
      {footer && (
        <div className="landscape:mt-4 portrait:hidden flex items-center justify-center">
          <Footer />
        </div>
      )}
    </div>
  )
}

export default Collections
